import React, { Suspense } from "react";
//#region types
import { WintSuperkollAPIModel } from "wint-api-client/dist/types";
import { FormHandles } from "models/propTypes/genericFormProps";
import { StyleEnums } from "models/enums";
import { TranslationKeys } from "translations";
import { IconDefinitionConstants, EventConstants } from "utils/constants";
import { IncomingInvoiceFormTypes } from "models/formTypes";
import { IncomingInvoiceViewInnerProps } from "models/propTypes/incomingInvoicePropTypes";
//#endregion
//#region components
import {
  ViewPanel,
  PageLayouts,
  EntityTitle,
  useMountedState,
  Skeletons,
  Profiles,
  GenericCommentsView,
} from "components/common";
import lazyWithReload from "utils/lazyWithReload";
import IncomingInvoiceViewAlerts from "./alerts";
import IncomingInvoiceDueDateBanner from "./dueDateBanner";
import UnhandledInfoBanner from "./unhandledInfoBanner";
import IncomingInvoiceFooter from "./footer";
import IncomingInvoiceSimilarList from "./similarList";
import IncomingInvoiceStateFlow from "./stateFlow";
import useIncomingInvoiceDownload from "./useDownload";
import useIncomingInvoiceFileViewer from "./useFileViwer";
import IncomingInvoiceVouchers from "./vouchers";
import { useIncomingInvoiceService } from "serviceHooks";
import useIncomingInvoicePermissions from "./usePermissions";
import {
  CostForwardingMarkingProvider,
  isForwardableIncomingInvoice,
  useDocumentPageCostForwarding,
} from "components/costForwardingMarking";
//#endregion
import { LocalizationHelperInstance, EventHelper } from "utils/helpers";
import cssExports from "theme/styles/main.module.scss";

const IncomingInvoiceDetailsFormContainer = lazyWithReload(() => import("./forms/detailsFormContainer"));

const IncomingInvoiceViewInner: React.FC<IncomingInvoiceViewInnerProps> = (props) => {
  const incomingInvoiceService = useIncomingInvoiceService();
  const { getMounted } = useMountedState();

  const { invoice, loadIncomingInvoice, reloadKey: key } = props;
  const isUnhandled =
    invoice?.state === WintSuperkollAPIModel.IncomingInvoiceState.Unhandled ||
    invoice?.state === WintSuperkollAPIModel.IncomingInvoiceState.SentForProcessing;
  const isUnhandledWithNoSupplier = isUnhandled && !invoice?.supplier?.id;
  const formRef = React.useRef<FormHandles>(null);

  const costForwarding = useDocumentPageCostForwarding(
    "incomingInvoice",
    invoice?.id,
    invoice?.supplier?.name ?? invoice?.supplierName,
    isForwardableIncomingInvoice(invoice),
    invoice?.state
  );

  const onInvoiceSubmit = (submitFunc?: (values: any) => Promise<any>) => {
    return (
      (formRef.current && formRef.current.submit(costForwarding.withoutStaleMarking(submitFunc))) ||
      new Promise<void>((resolve) => resolve())
    );
  };

  const { getHasIncomingInvoiceSigneePermission } = useIncomingInvoicePermissions();
  const hasSigneePermission = getHasIncomingInvoiceSigneePermission();
  const dueIn = incomingInvoiceService.getDueIn(invoice?.dueDate);

  const [displayType, setDisplayType] = React.useState<StyleEnums.DisplayType>(StyleEnums.DisplayType.Info);
  const [hasDueDateWarning, setHasDueDateWarning] = React.useState(false);
  const [isPaymentDetailsAccepted, setIsPaymentDetailsAccepted] = React.useState<boolean>(false);

  const dueDate = invoice?.dueDate;
  const toPayDate = invoice?.toPayDate;
  React.useEffect(() => {
    initializeUI();
  }, [invoice?.state, dueDate, toPayDate]);

  const initializeUI = () => {
    if (invoice) {
      const uiState = incomingInvoiceService.getUIDisplayState(invoice.state, invoice.paymentStatus);

      if (getMounted()) {
        setHasDueDateWarning(uiState.hasDueDateWarning);
        setDisplayType(uiState.displayType);
      }
    }
  };

  const reloadInvoice = (
    onSuccess?: (incomingInvoice: WintSuperkollAPIModel.IncomingInvoice) => void,
    onError?: () => void
  ) => {
    loadIncomingInvoice(onSuccess, onError);
  };

  const onUpdatePaymentDialogSuccess = (
    values: IncomingInvoiceFormTypes.UpdatePayment,
    onSuccess?: (incomingInvoice: WintSuperkollAPIModel.IncomingInvoice) => void
  ) => {
    if (invoice) {
      formRef.current?.setValues &&
        formRef.current.setValues({
          paymentStatus:
            invoice.state === WintSuperkollAPIModel.IncomingInvoiceState.Paid
              ? LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Paid)
              : LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Unpaid),
          paymentDate: values.paymentDate,
        });

      onSuccess &&
        onSuccess({
          ...invoice,
          paymentDate: values.paymentDate,
        });
    }
  };

  const onDownloadClick = useIncomingInvoiceDownload(invoice?.id ?? 0);

  const onTitleClick = () => {
    EventHelper.publishEvent(EventConstants.CloseDrawer);
  };

  const onPaymentDetailsAccepted = (checked: boolean) => {
    setIsPaymentDetailsAccepted(checked);
  };

  const upperLeftComponent = React.useMemo(
    () => (
      <EntityTitle
        key={invoice?.id}
        status={invoice ? incomingInvoiceService.getStateText(incomingInvoiceService.toViewState(invoice.state)) : ""}
        subTitle={
          invoice
            ? LocalizationHelperInstance.getFormattedMessage(
                isUnhandledWithNoSupplier
                  ? TranslationKeys.IncomingInvoice.IncomingInvoice
                  : TranslationKeys.IncomingInvoice.IncomingInvoiceFrom,
                "",
                {
                  id: `${invoice.oCR}`,
                }
              )
            : ""
        }
        title={
          isUnhandledWithNoSupplier ? (
            <></>
          ) : (
            <Profiles.SupplierProfileLink
              id={invoice?.supplier?.id}
              name={invoice?.supplier?.name}
              onProfileClick={onTitleClick}
              className={cssExports.titleLink}
            />
          )
        }
        loading={invoice === undefined}
      />
    ),
    [invoice]
  );

  const upperRightComponent = React.useMemo(
    () =>
      invoice ? (
        <IncomingInvoiceStateFlow
          key={`stateFlow-${invoice?.id}-${invoice.state}`}
          invoiceId={invoice.id}
          currentState={invoice.state}
        />
      ) : (
        <Skeletons.StateFlowSkeleton key="steps-skeleton" stepCount={6} />
      ),
    [invoice]
  );

  const fileViewerComponent = useIncomingInvoiceFileViewer(invoice?.id, invoice?.attachments, onDownloadClick);

  return (
    <>
      <PageLayouts.EntityViewLayout
        displayType={displayType}
        upperLeftComponent={upperLeftComponent}
        upperRightComponent={upperRightComponent}
        lowerLeftComponent={fileViewerComponent}
        footer={
          !isUnhandled && invoice ? (
            <IncomingInvoiceFooter
              invoice={invoice}
              invoiceFormSubmitHandler={onInvoiceSubmit}
              reloadHandler={reloadInvoice}
              markAsDoneHandler={reloadInvoice}
              onUpdatePaymentDialogSuccessHandler={onUpdatePaymentDialogSuccess}
              isInternationalPayment={invoice.isInternationalPayment}
              disableAccept={invoice.isInternationalPayment && hasSigneePermission && !isPaymentDetailsAccepted}
              showAcceptButtonTooltip={
                invoice.isInternationalPayment && hasSigneePermission && !isPaymentDetailsAccepted
              }
              acceptButtonTooltipText={LocalizationHelperInstance.getFormattedMessage(
                TranslationKeys.IncomingInvoice.InternationalPaymentAndAddressToolTipText
              )}
              costForwardingAction={costForwarding.menuAction}
            />
          ) : undefined
        }
      >
        <>
          <IncomingInvoiceDueDateBanner
            hidden={isUnhandled || !(invoice?.dueDate && hasDueDateWarning)}
            displayType={displayType}
            dueDate={invoice?.dueDate ?? new Date()}
            dueIn={dueIn ?? 0}
          />
          <UnhandledInfoBanner hidden={!isUnhandled} />
          <CostForwardingMarkingProvider value={costForwarding.context}>
            <ViewPanel
              title={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Details)}
              icon={IconDefinitionConstants.WEdit}
              classes={{ content: cssExports.detailsFormContainer }}
            >
              {invoice && key > 0 ? (
                <Suspense fallback={<Skeletons.FormSkeleton />}>
                  <IncomingInvoiceDetailsFormContainer
                    key={key}
                    ref={formRef}
                    invoice={invoice}
                    onUpdatePaymentDialogSuccessHandler={onUpdatePaymentDialogSuccess}
                    onPaymentDetailsAcceptHandler={onPaymentDetailsAccepted}
                    fileViewer={fileViewerComponent}
                    loadInvoice={reloadInvoice}
                  />
                </Suspense>
              ) : (
                <Skeletons.FormSkeleton />
              )}
            </ViewPanel>
          </CostForwardingMarkingProvider>

          {invoice && !isUnhandled ? (
            <IncomingInvoiceVouchers key={`vouchers-${invoice.state}`} id={invoice.id} />
          ) : (
            <></>
          )}

          {isUnhandled ? (
            <></>
          ) : (
            <>
              <GenericCommentsView
                key={`comments-${invoice?.state}`}
                parentId={invoice?.id ?? 0}
                type={WintSuperkollAPIModel.GenericCommentType.IncomingInvoice}
                loading={invoice === undefined}
              />
              {invoice && (
                <IncomingInvoiceSimilarList key={`similarList-${invoice.amount}-${invoice.currency}`} id={invoice.id} />
              )}
            </>
          )}
        </>
      </PageLayouts.EntityViewLayout>

      {invoice && !isUnhandled ? <IncomingInvoiceViewAlerts invoice={invoice} /> : <></>}
    </>
  );
};

export default IncomingInvoiceViewInner;
